import Link from 'next/link';

import type {
  PublishedLineup,
  PublishedLineupPlayer
} from '@api-service/predicted-lineups';
import { ARTICLE_TOKENS } from '@components/article/design-tokens';
import { PlayerAvatar } from '@components/predicted-lineups/PlayerAvatar';
import { toPlayerByIdPath } from '@lib/page-utils/player-profile/slugs';
import {
  buildFormationRows,
  isDoubtfulStarter
} from '@lib/predicted-lineups/compose';
import type {
  PlayerFacts,
  PlayerFactsMap
} from '@lib/predicted-lineups/player-facts';
import { teamColoursFor } from '@lib/predicted-lineups/team-colours';
import type { TeamColours } from '@lib/predicted-lineups/team-colours';

const PITCH_IMAGE = '/images/predicted-lineups-pitch.jpg';
const PITCH_ASPECT_RATIO = '3 / 4';
// Full column width made one club fill a desktop screen; capped, a pitch is
// about 640px tall and the words start above the fold.
const PITCH_MAX_WIDTH_CLASS = 'max-w-[480px]';
const PRICE_DECIMALS = 1;

const STATUS_LABEL: Partial<Record<PlayerFacts['status'], string>> = {
  d: 'Doubt',
  i: 'Injured',
  s: 'Suspended',
  u: 'Unavailable',
  n: 'Not available'
};

const formatPrice = (price: number) => `£${price.toFixed(PRICE_DECIMALS)}m`;
const OWNERSHIP_DECIMALS = 1;
const formatOwnership = (ownership: number) =>
  `${ownership.toFixed(OWNERSHIP_DECIMALS)}%`;

/** Short enough for a chip: the chance of playing when the feed has one, else the status. */
const doubtLabel = (facts: PlayerFacts | undefined) => {
  if (facts?.chanceOfPlaying !== null && facts?.chanceOfPlaying !== undefined) {
    return `${facts.chanceOfPlaying}% chance`;
  }
  return (facts && STATUS_LABEL[facts.status]) ?? 'Doubt';
};

const PlayerChip = ({
  player,
  facts,
  isDoubtful,
  colours
}: {
  player: PublishedLineupPlayer;
  facts?: PlayerFacts;
  isDoubtful: boolean;
  colours: TeamColours;
}) => (
  <Link
    href={toPlayerByIdPath(player.playerId)}
    className="flex w-[64px] flex-col items-center gap-1 no-underline sm:w-[84px]"
    data-cy="lineup-player"
    data-doubt={isDoubtful ? 'true' : undefined}
  >
    <span className="relative">
      <PlayerAvatar
        imageUrl={facts?.imageUrl}
        className="h-[42px] w-[42px] shadow sm:h-[52px] sm:w-[52px]"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-full"
        style={{
          boxShadow: isDoubtful
            ? '0 0 0 3px #f5a524'
            : `0 0 0 2px ${colours.background}`
        }}
      />
      {player.shirtNumber !== null && (
        <span
          className="absolute -left-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full px-1 font-body text-[10px] font-bold tabular-nums shadow"
          style={{ background: colours.background, color: colours.text }}
          aria-label={`Shirt number ${player.shirtNumber}`}
        >
          {player.shirtNumber}
        </span>
      )}
      {isDoubtful && (
        <span
          className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#f5a524] font-body text-[11px] font-bold text-[#14171c] shadow"
          aria-label="Fitness doubt"
        >
          ?
        </span>
      )}
    </span>
    <span className="max-w-full truncate rounded bg-black/70 px-1.5 py-0.5 text-center text-[11px] font-semibold leading-tight text-white sm:text-xs">
      {player.name}
    </span>
    {isDoubtful ? (
      <span
        className="rounded bg-[#f5a524] px-1.5 py-0.5 font-body text-[10px] font-semibold leading-none text-[#14171c]"
        data-cy="lineup-player-doubt"
      >
        {doubtLabel(facts)}
      </span>
    ) : (
      facts && (
        <span
          className="flex gap-1 font-body text-[10px] font-semibold leading-none tabular-nums"
          data-cy="lineup-player-facts"
        >
          <span className="rounded bg-black/45 px-1 py-0.5 text-white">
            {formatPrice(facts.price)}
          </span>
          {facts.ownership !== null && (
            <span className="rounded bg-white/90 px-1 py-0.5 text-[#14171c]">
              {formatOwnership(facts.ownership)}
            </span>
          )}
        </span>
      )
    )}
  </Link>
);

type LineupPitchProps = {
  lineup?: PublishedLineup;
  playerFacts: PlayerFactsMap;
  clubName: string;
  /** Opta ids the editor listed under Doubts for this club. */
  doubtOptaIds?: ReadonlySet<string>;
};

const NO_DOUBTS: ReadonlySet<string> = new Set();

/**
 * The eleven drawn from the API's tactical roles. Each chip carries the two
 * numbers a manager reads against a name, price and ownership, and a doubt
 * swaps them for the reason. Position needs no label: the row says it. The
 * club's colour marks the number badge and the ring so a reader skimming
 * twenty pitches can tell them apart. Every club's pitch is public; only the
 * words around it can be gated.
 */
export const LineupPitch = ({
  lineup,
  playerFacts,
  clubName,
  doubtOptaIds = NO_DOUBTS
}: LineupPitchProps) => {
  if (!lineup) {
    return (
      <div
        className={`my-6 rounded-[14px] border p-6 text-center text-sm ${ARTICLE_TOKENS.rule} ${ARTICLE_TOKENS.surfaceTint} ${ARTICLE_TOKENS.bodyMuted}`}
        data-cy="lineup-pitch-placeholder"
      >
        The predicted {clubName} lineup will appear here once it is published.
      </div>
    );
  }

  const rows = buildFormationRows(lineup.players);
  const colours = teamColoursFor(lineup.teamShortName);

  return (
    <figure
      className={`mx-auto my-6 ${PITCH_MAX_WIDTH_CLASS}`}
      data-cy="lineup-pitch"
    >
      <div
        className="relative overflow-hidden rounded-[14px] bg-[#2f7d4a] bg-cover bg-center"
        style={{
          backgroundImage: `url(${PITCH_IMAGE})`,
          aspectRatio: PITCH_ASPECT_RATIO
        }}
      >
        <span className="absolute right-3 top-3 rounded-full bg-black/60 px-2.5 py-1 text-xs font-semibold text-white">
          {lineup.formationDescriptor.label}
        </span>
        <div className="absolute inset-0 flex flex-col justify-between px-2 pb-4 pt-10 sm:px-4 sm:pb-5 sm:pt-11">
          {rows.map((row, index) => (
            <div key={index} className="flex items-start justify-around">
              {row.map((player) => (
                <PlayerChip
                  key={player.playerId}
                  player={player}
                  facts={playerFacts[player.playerId]}
                  colours={colours}
                  isDoubtful={isDoubtfulStarter(
                    player,
                    playerFacts[player.playerId],
                    doubtOptaIds
                  )}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </figure>
  );
};
